import './index.less';
import classNames from 'classnames';
import { isBoolean, isNull, isNumber } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import {
    BaseLayerView,
    parseLayerId,
    useActiveOriginIndex,
    useFramework,
    useIsLongImageMode,
    useLayerTransform,
    useScale,
} from '@/framework';
import { BaseOperateView, BaseStagePlugin } from '@/framework/operate';
import { registerStyleSheet } from '@/utils/common-kit';
import { IExportPlugin, ILayerProps, ISize, PluginType } from '@/version-types';
import { SvipWatermarkPlugin } from './svip-watermark';

type StageWatermarkProps = Map<number, { watermarkLayers: ILayerProps[]; isGlobal: boolean }>;

export const PreviewSvipWatermarkView: React.FC<{
    stageIndex: number;
    fetchLayers?: (layerId: string[]) => ILayerProps[];
}> = React.memo(({ stageIndex, fetchLayers }) => {
    const framework = useFramework();
    const watermarkDataRef = useRef<StageWatermarkProps | null | undefined>(
        framework.getInstance(SvipWatermarkPlugin).getWatermarkData(),
    );

    if (!watermarkDataRef.current) return null;

    const res = watermarkDataRef.current.get(stageIndex);
    if (!res) return null;

    // 全局水印
    if (res.isGlobal) {
        const size = framework.getSizeByIndex(stageIndex);
        const backgroundSize = Math.round((size.width < size.height ? size.width : size.height) / 3);
        return <div className="svip-watermark-global svip-watermark-image" style={{ backgroundSize }}></div>;
    }

    // 图层水印
    if (res.watermarkLayers.length === 0) return null;
    const layers: ILayerProps[] = [];
    if (fetchLayers) {
        const layerIds = res.watermarkLayers.map((layer) => layer.id);
        layers.push(...fetchLayers(layerIds));
    } else {
        res.watermarkLayers.forEach((layer) => {
            layers.push(framework.getLayer(layer.id));
        });
    }
    return (
        <>
            {layers.map((layer) => {
                let backgroundSize = layer.width > layer.height ? layer.height : layer.width;
                let backgroundPosition = 'left top';
                if (backgroundSize < 100) {
                    backgroundSize = 100;
                    backgroundPosition = 'center';
                }
                const { __animation } = layer;
                return (
                    <div
                        key={layer.id}
                        style={{
                            ...(__animation?.enabled && fetchLayers
                                ? {
                                      opacity: 0,
                                      animation: `${__animation?.name} ${__animation?.duration || 0.2}s ease-in ${__animation?.delay}s forwards`,
                                  }
                                : {}),
                        }}
                    >
                        <BaseLayerView layer={layer}>
                            <div
                                style={{ backgroundSize, backgroundPosition }}
                                className={classNames('svip-watermark-layer svip-watermark-image')}
                            ></div>
                        </BaseLayerView>
                    </div>
                );
            })}
        </>
    );
});

export const SvipWatermarkView: React.FC = React.memo(() => {
    const framework = useFramework();
    const [watermarkData, setWatermarkData] = useState<StageWatermarkProps>(
        framework.getInstance(SvipWatermarkPlugin).getWatermarkData(),
    );
    const [activeLayerWatermark, setActiveLayerWatermark] = useState<boolean>(false);
    useEffect(() => {
        const handler = framework.getInstance(SvipWatermarkPlugin).onWatermark((data, activeLayerWatermark) => {
            setWatermarkData(data);
            if (isBoolean(activeLayerWatermark)) {
                setActiveLayerWatermark(activeLayerWatermark);
            }
        });
        return () => framework.events.off(handler);
    }, []);
    const isLongImageMode = useIsLongImageMode();

    if (!watermarkData.size && !activeLayerWatermark) return null;

    // 长图模式，全局水印就显示特殊处理
    if (isLongImageMode) {
        const entries = watermarkData.entries();
        for (const [, { isGlobal }] of entries) {
            if (isGlobal) return <GlobalWatermark />;
        }
    }

    // 图层水印
    return (
        <>
            {activeLayerWatermark ? <ActiveLayerWatermark data={watermarkData} /> : null}
            <StageWatermark data={watermarkData} />
        </>
    );
});

const GlobalWatermark = React.memo(() => {
    const framework = useFramework();
    const [size, setSize] = useState<ISize | null>(null);
    const originIndex = useActiveOriginIndex();
    const scale = useScale();

    useEffect(() => {
        const handler = framework.events.onStageResize(() => {
            setSize(framework.stage.getSize());
        });
        setSize(framework.stage.getSize());
        return () => framework.events.off(handler);
    }, []);

    if (isNull(size)) return null;
    const backgroundSize = Math.round((size.width < size.height ? size.width : size.height) / 3) * scale;
    return (
        <BaseStagePlugin name="svip-watermark-global" isWrapOnly={true} originIndexOrLayerId={originIndex}>
            <div className="svip-watermark-global svip-watermark-image" style={{ backgroundSize }}></div>
        </BaseStagePlugin>
    );
});

const StageWatermark: React.FC<{ data: StageWatermarkProps }> = ({ data }) => {
    const isLongImageMode = useIsLongImageMode();
    return isLongImageMode ? <VarietyStageNode data={data} /> : <SingleStageNode data={data} />;
};

/**
 * 渲染单个舞台
 */
const SingleStageNode: React.FC<{ data: StageWatermarkProps }> = React.memo(({ data }) => {
    const framework = useFramework();
    const originIndex = useActiveOriginIndex();
    const stageIndex = framework.convertOriginToStage(originIndex);
    if (data.get(stageIndex)?.isGlobal) {
        return <GlobalWatermark />;
    }
    const watermarkLayers = data.get(stageIndex)?.watermarkLayers || [];
    if (!watermarkLayers.length) return null;
    return <RenderStageView originIndex={originIndex} layers={watermarkLayers} />;
});

/**
 * 渲染长图舞台
 */
const VarietyStageNode: React.FC<{ data: StageWatermarkProps }> = React.memo(({ data }) => {
    const entries = data.entries();
    const framework = useFramework();
    return (
        <>
            {Array.from(entries).map(([stageIndex, { watermarkLayers }]) => {
                const originIndex = framework.convertStageToOrigin(stageIndex);
                return <RenderStageView key={originIndex} originIndex={originIndex} layers={watermarkLayers} />;
            })}
        </>
    );
});

/**
 * 渲染一个指定舞台
 */
const RenderStageView: React.FC<{
    originIndex: number;
    layers: ILayerProps[];
}> = ({ originIndex, layers }) => {
    if (!layers.length) return null;
    const layerOriginIndex = parseLayerId(layers[0].id || '').originIndex;
    if (layerOriginIndex !== originIndex) return null;
    return (
        <BaseStagePlugin name={`svip-watermark-stage-${originIndex}`} originIndexOrLayerId={originIndex}>
            <div className="svip-watermark-layer-wrap">
                {layers.map((layer) => {
                    return <LayerNode key={layer.id} layerId={layer.id} />;
                })}
            </div>
        </BaseStagePlugin>
    );
};

const LayerNode: React.FC<{ layerId: string }> = React.memo(({ layerId }) => {
    const framework = useFramework();
    const [layer, setLayer] = useState(framework.getLayer(layerId));
    useEffect(() => {
        const handler = framework.events.onRerenderLayer(layerId, () => {
            setLayer(framework.getLayer(layerId));
        });
        return () => {
            framework.events.off(handler);
        };
    }, [layerId]);
    return <LayerView layer={layer} />;
});

const LayerView: React.FC<{ layer: ILayerProps }> = ({ layer: originLayer }) => {
    const scale = useScale();
    const framework = useFramework();
    const refNode = useLayerTransform(originLayer?.id);
    const layer = framework.getLayer(originLayer?.id);
    if (!layer || layer?.__isHidden) return null;

    let backgroundSize = layer.width > layer.height ? layer.height : layer.width;
    let backgroundPosition = 'left top';
    if (backgroundSize < 100) {
        backgroundSize = 100;
        backgroundPosition = 'center';
    }

    backgroundSize *= scale;

    return (
        <BaseOperateView ref={refNode} layer={layer}>
            <div
                style={{ backgroundSize, backgroundPosition }}
                className={classNames('svip-watermark-layer svip-watermark-image')}
            ></div>
        </BaseOperateView>
    );
};

const ActiveLayerWatermark: React.FC<{ data: StageWatermarkProps }> = ({ data }) => {
    const framework = useFramework();
    const [layer, setLayer] = useState<ILayerProps | null>(framework.operate.getActivePickLayer());
    useEffect(() => {
        const handle = framework.events.onActiveLayerDataUpdate((layer, pickLayer) => {
            setLayer(pickLayer || layer);
        });
        return () => framework.events.off(handle);
    }, []);
    if (!layer) return null;
    const originIndex = parseLayerId(layer.id || '').originIndex;
    if (!isNumber(originIndex)) return null;
    const res = data.get(framework.stage.getActiveStageIndex())?.watermarkLayers.find((v) => v.id === layer.id);
    if (res) return null;
    return <RenderStageView originIndex={originIndex} layers={[layer]} />;
};

const RegisterStyleSheet = React.memo(() => {
    const framework = useFramework();
    useEffect(() => {
        const remove = registerStyleSheet(`
            .svip-watermark-image {
                background-image: url(${framework.getInstance(SvipWatermarkPlugin).getWatermarkImage()});
            }
        `);
        return () => remove();
    }, []);
    return null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 1,
    name: 'svip-watermark',
    render() {
        return (
            <>
                <RegisterStyleSheet />
                <SvipWatermarkView />
            </>
        );
    },
    createInstance({ identity }) {
        return [{ key: 'SvipWatermarkPlugin', value: new SvipWatermarkPlugin(identity) }];
    },
};

export default exportPlugin;
